import { useCallback, useEffect, useMemo, useState } from "react";
import { ReactFlow, Background, Controls, MarkerType, Handle, Position, applyNodeChanges, applyEdgeChanges, addEdge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { toast } from "sonner";
import { Plus, Save, Upload, Trash2, RotateCcw, History, X, AlertTriangle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime } from "@/lib/format";
import { PageHeader, SelectBox, Panel } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const TYPE_STYLE = { open: "border-[#0B192C]", won: "border-emerald-500", lost: "border-red-400" };
const edgeStyle = (e) => ({ ...e, type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed, color: "#64748B" }, style: { stroke: "#64748B", strokeWidth: 1.6 } });

function StageNode({ data, selected }) {
  const s = data.stage;
  return (
    <div className={`w-[190px] bg-white rounded-md border-2 ${TYPE_STYLE[s.type]} ${s.active ? "" : "opacity-50 border-dashed"} ${selected ? "ring-2 ring-orange-400" : ""} shadow-sm`} data-testid={`builder-node-${s.id}`}>
      <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2.5 !h-2.5" />
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between">
          <span className={`text-[9px] font-bold tracking-wider uppercase ${s.type === "won" ? "text-emerald-600" : s.type === "lost" ? "text-red-500" : "text-orange-600"}`}>{s.type}{!s.active && " · inactive"}</span>
          <span className="num text-[10px] bg-slate-100 rounded px-1">{data.count} leads</span>
        </div>
        <div className="text-sm font-semibold mt-1 leading-tight">{s.name || "Untitled"}</div>
        {s.required_fields.length > 0 && <div className="text-[10px] text-slate-400 mt-1">{s.required_fields.length} required field(s)</div>}
      </div>
      {s.type === "open" && <Handle type="source" position={Position.Right} className="!bg-orange-500 !w-2.5 !h-2.5" />}
    </div>
  );
}
const nodeTypes = { stage: StageNode };

export function orderStages(stages, edges) {
  const act = stages.filter((s) => s.active);
  const opens = act.filter((s) => s.type === "open");
  const ids = new Set(opens.map((s) => s.id));
  const nxt = {};
  const incoming = new Set();
  edges.forEach((e) => {
    if (ids.has(e.source) && ids.has(e.target) && !nxt[e.source]) { nxt[e.source] = e.target; incoming.add(e.target); }
  });
  const byX = (a, b) => a.position.x - b.position.x;
  const start = opens.filter((s) => !incoming.has(s.id)).sort(byX)[0];
  const seen = [];
  let cur = start?.id;
  while (cur && !seen.includes(cur)) { seen.push(cur); cur = nxt[cur]; }
  const rest = opens.filter((s) => !seen.includes(s.id)).sort(byX);
  return [...seen.map((id) => act.find((s) => s.id === id)), ...rest, ...act.filter((s) => s.type === "won"), ...act.filter((s) => s.type === "lost")];
}

function Inspector({ node, fields, onChange, onDelete, onClose }) {
  const s = node.data.stage;
  const set = (patch) => onChange({ ...s, ...patch });
  const toggleField = (k) => set({ required_fields: s.required_fields.includes(k) ? s.required_fields.filter((x) => x !== k) : [...s.required_fields, k] });
  return (
    <div className="space-y-4" data-testid="builder-inspector">
      <div className="flex items-center justify-between"><span className="eyebrow">Stage settings</span><button onClick={onClose}><X className="h-4 w-4" /></button></div>
      <div className="space-y-1.5"><Label>Name</Label><Input value={s.name} onChange={(e) => set({ name: e.target.value })} data-testid="builder-stage-name-input" /></div>
      <div className="space-y-1.5"><Label>Type</Label>
        <SelectBox value={s.type} onChange={(v) => set({ type: v || "open" })} testid="builder-stage-type-select" className="w-full" options={[{ value: "open", label: "Open (in progress)" }, { value: "won", label: "Won (closing)" }, { value: "lost", label: "Lost (closing)" }]} />
      </div>
      <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={s.active} onCheckedChange={(v) => set({ active: v })} data-testid="builder-stage-active-switch" /></div>
      <div>
        <Label>Required before entering this stage</Label>
        <div className="mt-2 grid grid-cols-1 gap-1.5 max-h-56 overflow-y-auto">
          {fields.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm"><Checkbox checked={s.required_fields.includes(f.key)} onCheckedChange={() => toggleField(f.key)} data-testid={`builder-required-${f.key}`} />{f.label}</label>
          ))}
        </div>
      </div>
      <div className="text-xs text-slate-500">{node.data.count > 0 ? `${node.data.count} lead(s) are in this stage. Deactivating or deleting it will require migrating them when you publish.` : "No leads in this stage."}</div>
      <Button variant="outline" className="w-full text-red-600" onClick={onDelete} data-testid="builder-delete-stage-button"><Trash2 className="h-4 w-4 mr-1" />Remove stage</Button>
    </div>
  );
}

function PublishDialog({ open, onOpenChange, market, stages, onPublished }) {
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(null);
  const [mig, setMig] = useState({});
  useEffect(() => { if (open) { setNote(""); setPending(null); setMig({}); } }, [open]);
  const active = stages.filter((s) => s.active);
  const publish = async () => {
    try {
      const { data } = await api.post(`/workflows/${market}/publish`, { note, migrations: mig });
      toast.success(`Published version ${data.version}`);
      onOpenChange(false);
      onPublished();
    } catch (e) {
      if (e.response?.status === 409) setPending(e.response.data.detail.stages);
      else toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="publish-dialog">
        <DialogHeader><DialogTitle>Publish workflow</DialogTitle></DialogHeader>
        <div className="space-y-1.5"><Label>Version note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Added site inspection stage" data-testid="publish-note-input" /></div>
        {pending && (
          <div className="border border-amber-300 bg-amber-50 rounded p-3 space-y-3" data-testid="migration-panel">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" />Move leads out of removed / inactive stages</div>
            {pending.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 text-sm">
                <span>{p.name} <span className="num text-xs">({p.count})</span> →</span>
                <SelectBox value={mig[p.id]} onChange={(v) => setMig({ ...mig, [p.id]: v })} testid={`migrate-select-${p.id}`} className="w-48" allLabel="Choose stage" options={active.map((s) => ({ value: s.id, label: s.name }))} />
              </div>
            ))}
            <p className="text-xs text-amber-700">Lead histories are preserved; each moved lead gets a migration entry in its activity log.</p>
          </div>
        )}
        <DialogFooter><Button onClick={publish} className="bg-orange-500 hover:bg-orange-600" data-testid="publish-confirm-button">{pending ? "Migrate & publish" : "Publish"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FunnelBuilder() {
  const { meta, refreshMeta } = useAuth();
  const [market, setMarket] = useState("dealer");
  const [wf, setWf] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/workflows/${market}`);
    setWf(data);
    const src = data.draft || data.published;
    setNodes(src.stages.map((s) => ({ id: s.id, type: "stage", position: s.position, data: { stage: s, count: data.stage_counts[s.id] || 0 } })));
    setEdges(src.edges.map(edgeStyle));
    setErrors(data.draft?.errors || []);
    setDirty(false);
    setSelected(null);
  }, [market]);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const stages = useMemo(() => nodes.map((n) => ({ ...n.data.stage, position: { x: Math.round(n.position.x), y: Math.round(n.position.y) } })), [nodes]);
  const preview = useMemo(() => orderStages(stages, edges), [stages, edges]);
  const m = meta.markets.find((x) => x.key === market);
  const fields = [...meta.core_fields, ...m.fields.filter((f) => f.active)];
  const selNode = nodes.find((n) => n.id === selected);

  const onNodesChange = (ch) => { setNodes((ns) => applyNodeChanges(ch, ns)); if (ch.some((c) => c.type === "position" || c.type === "remove")) setDirty(true); };
  const onEdgesChange = (ch) => { setEdges((es) => applyEdgeChanges(ch, es)); if (ch.some((c) => c.type === "remove")) setDirty(true); };
  const onConnect = (c) => { setEdges((es) => addEdge(edgeStyle({ ...c, id: `e_${c.source}_${c.target}` }), es)); setDirty(true); };

  const updateStage = (s) => { setNodes((ns) => ns.map((n) => (n.id === s.id ? { ...n, data: { ...n.data, stage: s } } : n))); setDirty(true); };
  const removeStage = () => {
    setNodes((ns) => ns.filter((n) => n.id !== selected));
    setEdges((es) => es.filter((e) => e.source !== selected && e.target !== selected));
    setSelected(null);
    setDirty(true);
  };
  const addStage = () => {
    const id = `${market.slice(0, 3)}_${Date.now().toString(36)}`;
    const maxX = Math.max(0, ...nodes.map((n) => n.position.x));
    setNodes((ns) => [...ns, { id, type: "stage", position: { x: maxX / 2, y: 330 }, data: { stage: { id, name: "New stage", type: "open", active: true, required_fields: [], position: { x: 0, y: 0 } }, count: 0 } }]);
    setSelected(id);
    setDirty(true);
  };

  const saveDraft = async () => {
    try {
      const { data } = await api.put(`/workflows/${market}/draft`, { stages, edges: edges.map(({ id, source, target }) => ({ id, source, target })) });
      setErrors(data.errors);
      setDirty(false);
      const w = await api.get(`/workflows/${market}`);
      setWf(w.data);
      data.errors.length ? toast.warning("Draft saved with issues to fix before publishing") : toast.success("Draft saved");
      return data;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const openPublish = async () => {
    const d = await saveDraft();
    if (d && !d.errors.length) setPublishOpen(true);
  };
  const discard = async () => {
    await api.delete(`/workflows/${market}/draft`);
    toast.success("Draft discarded");
    load();
  };
  const restore = async (v) => {
    try {
      await api.post(`/workflows/${market}/versions/${v.id}/restore`);
      toast.success(`Version ${v.version} loaded into draft. Review and publish to apply.`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const published = () => { load(); refreshMeta(); };

  if (!wf) return <div className="text-slate-400">Loading builder…</div>;
  const hasDraft = !!wf.draft || dirty;

  return (
    <div data-testid="funnel-builder-page">
      <PageHeader eyebrow="Configuration" title="Funnel Builder" subtitle="Drag stages, connect them with arrows (linear flow + won/lost branches), set required fields, preview and publish. Every publish is versioned and restorable.">
        <Button variant="outline" onClick={addStage} data-testid="builder-add-stage-button"><Plus className="h-4 w-4 mr-1" />Add stage</Button>
        <Button variant="outline" onClick={saveDraft} disabled={!dirty} data-testid="builder-save-draft-button"><Save className="h-4 w-4 mr-1" />Save draft</Button>
        {wf.draft && <Button variant="outline" onClick={discard} data-testid="builder-discard-draft-button"><RotateCcw className="h-4 w-4 mr-1" />Discard draft</Button>}
        <Button onClick={openPublish} disabled={!hasDraft} className="bg-orange-500 hover:bg-orange-600" data-testid="builder-publish-button"><Upload className="h-4 w-4 mr-1" />Publish</Button>
      </PageHeader>

      <Tabs value={market} onValueChange={(v) => { if (!dirty || window.confirm("Discard unsaved changes?")) setMarket(v); }} className="mb-4">
        <TabsList className="bg-white border">
          {meta.markets.map((x) => <TabsTrigger key={x.key} value={x.key} data-testid={`builder-tab-${x.key}`}>{x.name}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap items-center gap-2 text-sm mb-3" data-testid="builder-status">
        <span className="px-2 py-0.5 rounded bg-[#0B192C] text-white text-xs">Published v{wf.published.version}</span>
        {hasDraft ? <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-700 text-xs font-semibold">Editing draft{wf.draft?.restored_from ? ` (restored from v${wf.draft.restored_from})` : ""}{dirty ? " · unsaved changes" : ""}</span> : <span className="text-xs text-slate-500">Showing the currently published workflow</span>}
      </div>
      {errors.length > 0 && (
        <div className="border border-red-200 bg-red-50 text-red-700 text-sm rounded p-3 mb-3" data-testid="builder-errors">{errors.map((e) => <div key={e}>• {e}</div>)}</div>
      )}

      <div className="grid xl:grid-cols-[1fr_300px] gap-4">
        <div className="h-[480px] bg-white border rounded-md" data-testid="builder-canvas">
          <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
            onNodeClick={(_, n) => setSelected(n.id)} onPaneClick={() => setSelected(null)} fitView deleteKeyCode={["Delete"]}>
            <Background gap={24} color="#E2E8F0" />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
        <Panel>
          {selNode ? <Inspector node={selNode} fields={fields} onChange={updateStage} onDelete={removeStage} onClose={() => setSelected(null)} /> : (
            <div className="text-sm text-slate-500 space-y-2">
              <div className="eyebrow">How to edit</div>
              <p>Click a stage to rename, change type, deactivate or set required fields.</p>
              <p>Drag from the orange handle to another stage to connect. Select an arrow and press <kbd className="px-1 border rounded">Delete</kbd> to remove it.</p>
              <p>Closing stages (won / lost) cannot have outgoing arrows.</p>
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Preview — Kanban columns after publishing" className="mt-4" testid="builder-preview">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {preview.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2 shrink-0">
              <div className={`px-3 py-2 rounded border-t-[3px] bg-slate-50 text-sm ${s.type === "won" ? "border-t-emerald-500" : s.type === "lost" ? "border-t-red-400" : "border-t-[#0B192C]"}`}>{s.name}</div>
              {i < preview.length - 1 && <span className="text-slate-300">→</span>}
            </div>
          ))}
        </div>
      </Panel>

      <Panel title={<span className="flex items-center gap-2"><History className="h-4 w-4" />Version history</span>} className="mt-4" testid="builder-versions">
        <div className="divide-y">
          {wf.versions.map((v) => (
            <div key={v.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-2.5" data-testid={`version-row-${v.version}`}>
              <div className="text-sm">
                <b className="num">v{v.version}</b> · {v.note} {v.restored_from && <span className="text-xs text-slate-400">(restored from v{v.restored_from})</span>}
                <div className="text-xs text-slate-400">{v.created_by} · {dateTime(v.created_at)}</div>
              </div>
              {v.id === wf.market.published_version_id ? <span className="text-xs font-semibold text-emerald-700">Currently published</span> : (
                <Button size="sm" variant="outline" onClick={() => restore(v)} data-testid={`restore-version-${v.version}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore to draft</Button>
              )}
            </div>
          ))}
        </div>
      </Panel>

      <PublishDialog open={publishOpen} onOpenChange={setPublishOpen} market={market} stages={stages} onPublished={published} />
    </div>
  );
}
